export interface PrdSection {
  id: string;
  title: string;
  icon: string;
  color: string;
  content: PrdBlock[];
}

export interface PrdBlock {
  type: 'heading' | 'text' | 'list' | 'rule' | 'table' | 'interaction' | 'note';
  title?: string;
  text?: string;
  items?: string[];
  rows?: { label: string; desc: string }[];
  note?: string;
  steps?: string[];
}

export const PRD_SECTIONS: PrdSection[] = [
  {
    id: 'overview',
    title: '系统概述',
    icon: 'ri-briefcase-line',
    color: '#0069BD',
    content: [
      {
        type: 'heading',
        title: '产品定位',
      },
      {
        type: 'text',
        text: '赛诺威视代理商资料分发平台是一套面向医疗器械代理商的 B2B 数字资产管理系统。管理员在后台上传、分类、分级管理产品资料（PDF / PPT / 视频 / Word / Excel），代理商通过独立账号登录前端资料库进行浏览和下载。系统核心卖点包括：动态水印溯源、机密资料分级授权、产品型号级文件夹权限管控、全链路活动追踪。',
      },
      {
        type: 'heading',
        title: '用户角色',
      },
      {
        type: 'list',
        items: [
          '管理员：后台唯一操作者，负责创建代理商账号、上传资料、配置水印、管理权限、查看数据',
          '代理商：通过管理员分配的邮箱和密码登录前端资料库，浏览和下载已授权的资料',
        ],
      },
      {
        type: 'heading',
        title: '信息架构',
      },
      {
        type: 'table',
        rows: [
          { label: '数据概览', desc: '平台核心指标（代理商数、浏览量、下载量）和活动趋势图表' },
          { label: '代理商管理', desc: '代理商账号的创建、编辑、禁用、权限配置、活动追踪、数据导出' },
          { label: '资料管理', desc: '资料的上传、编辑、分类、分级（普通/机密）、下载权限、删除' },
          { label: '水印设置', desc: '在线预览水印内容配置和效果预览，文件类型策略总览' },
          { label: '用户操作手册', desc: '面向管理员的完整操作指南，按模块组织' },
        ],
      },
      {
        type: 'heading',
        title: '核心规则总览',
      },
      {
        type: 'rule',
        items: [
          '代理商账号由管理员手动创建，不支持自行注册',
          '资料分为「普通」和「机密」两级；普通资料对所有代理商可见，机密资料需单独授权',
          '所有文件在线预览时强制叠加动态水印，不可关闭',
          '下载水印仅 PDF 格式支持嵌入；PPT / Word / Excel 下载后为原始文件，无法嵌入水印',
          'E-learning 视频默认禁止下载，仅支持在线播放',
          '管理员可为每位代理商单独限定可见的产品型号文件夹范围',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    title: '数据概览',
    icon: 'ri-dashboard-3-line',
    color: '#374151',
    content: [
      {
        type: 'heading',
        title: '页面定位',
      },
      {
        type: 'text',
        text: '后台首页，展示平台整体运营情况的核心指标。管理员通过时间筛选器快速切换数据范围，所有图表和表格数据会联动更新。',
      },
      {
        type: 'heading',
        title: '页面结构',
      },
      {
        type: 'list',
        items: [
          '顶部时间筛选器：今日 / 7天 / 30天 / 1年 / 全部 — 分段控制器样式，选中项带阴影高亮',
          'KPI 卡片行（4列网格）：代理商总数（含活跃数子标签）、待审核申请、期间浏览量、期间下载量',
          '活动趋势图表：SVG 实现的平滑曲线图，展示浏览量（深色线）和下载量（浅色线）的双线趋势；支持鼠标悬停显示当日具体数值的浮动提示框',
          '底部双栏：左侧「代理商活动明细」表格（6条，按浏览量降序），右侧「热门资料排行」表格（5条，按浏览量降序）',
        ],
      },
      {
        type: 'heading',
        title: '交互规则',
      },
      {
        type: 'interaction',
        steps: [
          '点击时间筛选器任一选项，所有数据联动刷新（KPI、图表、两个表格）',
          '图表区域鼠标悬停时，在对应日期位置显示垂直参考线，并在鼠标附近显示浮动提示框（包含日期、浏览量、下载量）',
          '图表在「1年」模式下自动按周聚合数据点，减少密度避免重叠',
          'KPI 卡片的数字和表格数据根据时间范围按比例缩放（mock 数据策略）',
        ],
      },
      {
        type: 'heading',
        title: '数据规则',
      },
      {
        type: 'rule',
        items: [
          '代理商总数和待审核数不受时间筛选影响，始终显示全量数据',
          '期间浏览量/下载量根据选定时间窗口从 mockActivityData 中聚合计算',
          '图表数据为每日粒度，1年视图下超过60个点时自动转为周平均值',
        ],
      },
    ],
  },
  {
    id: 'agents',
    title: '代理商管理',
    icon: 'ri-team-line',
    color: '#059669',
    content: [
      {
        type: 'heading',
        title: '代理商列表页',
      },
      {
        type: 'text',
        text: '展示所有代理商账号的完整列表，支持搜索、筛选、批量操作和数据导出。表格采用首列（代理商信息）和末列（操作按钮）sticky 固定的布局，方便横向滚动时保持关键列可见。',
      },
      {
        type: 'heading',
        title: '列表页结构',
      },
      {
        type: 'list',
        items: [
          '工具栏：搜索框（按姓名/公司/邮箱模糊匹配）+ 状态筛选下拉框（全部/启用中/已禁用/已过期）+ 导出下拉菜单（导出当前筛选结果 / 导出全部 / 导出已选中）+ 新建代理商按钮',
          '表格列：代理商（姓名+邮箱头像）、公司、地区、状态标签、有效期至、浏览量、下载量、备注、操作',
          '备注列：有备注时显示便签图标，鼠标悬停显示完整内容浮层；无备注时显示「+」按钮点击添加内联备注弹窗',
          '操作列图标按钮：活动记录（跳转详情页）、重置密码（弹窗）、禁用/启用（状态相关）、编辑（跳转编辑页）',
        ],
      },
      {
        type: 'heading',
        title: '新建代理商',
      },
      {
        type: 'list',
        items: [
          '三栏布局：左侧预览卡片（实时反映填写内容），右侧表单',
          '基本信息区块：姓名、邮箱、公司名称、所在地区（下拉选择）',
          '授权时间区块：有效期选择（90天/180天/1年/2年），左侧预览实时显示到期日',
          '资料分类权限区块：FolderPermissionBlock 组件，按产品型号勾选；默认全部可见',
          '机密资料授权区块：列出所有机密资料，勾选授权；默认不勾选',
          '登录凭据区块：初始密码输入框 + 随机生成按钮 + 显隐切换',
          '备注区块：可选，500字上限',
          '提交后弹出成功弹窗，展示姓名/邮箱/密码，提示通过安全渠道线下发送',
        ],
      },
      {
        type: 'heading',
        title: '编辑代理商',
      },
      {
        type: 'text',
        text: '页面结构与新建类似，但左侧展示的是当前代理商的真实档案信息和活动数据统计。表单字段包含：姓名（可编辑）、公司（可编辑）、地区（可编辑）、状态（下拉）、账号有效期（支持快捷续期：+3月/+6月/+1年/+2年 或手动选日期）、产品型号文件夹权限、机密资料授权、备注。点击「保存修改」后显示成功提示并返回列表。',
      },
      {
        type: 'heading',
        title: '代理商详情页',
      },
      {
        type: 'list',
        items: [
          '四栏布局：左侧1/4为代理商档案卡片（头像、基本信息、社交账号、活动概览数字、备注编辑区），右侧3/4为标签页内容',
          '标签页1「资料权限」：三列统计卡片（普通资料数、机密授权数、可见分类数）+ 机密资料授权勾选列表',
          '标签页2「文件访问明细」：列出该代理商访问过的所有文件，每行显示缩略图、标题、分类、浏览次数、下载次数、最后访问日期；点击标题可跳转资料详情',
          '标签页3「分类权限设置」：FolderPermissionBlock 组件，可勾选/取消该代理商可见的产品型号',
        ],
      },
      {
        type: 'heading',
        title: '业务规则',
      },
      {
        type: 'rule',
        items: [
          '代理商状态包括：启用中(active)、待审核(pending)、已禁用(disabled)、已过期(expired)',
          '禁用账号立即失去所有访问权限，但数据保留，可随时重新启用',
          '已过期账号需手动续期后才能恢复为启用中',
          '普通资料自动对所有状态为 active 的代理商可见（在其有权限的分类范围内）',
          '机密资料仅对 confidentialsAgents 列表中包含的代理商可见',
          '文件夹权限(allowedModels)为空数组或无值时，表示全部可见',
        ],
      },
      {
        type: 'heading',
        title: '导出功能',
      },
      {
        type: 'text',
        text: '点击工具栏「导出」按钮展开下拉菜单，提供三种导出范围：「导出当前筛选结果」（受搜索和状态筛选影响）、「导出全部代理商」（系统全量）、「导出已选中」（需先在列表勾选目标行）。导出文件为 UTF-8 编码的 CSV，包含姓名、邮箱、公司、地区、状态、社交账号平台、社交账号名、有效期、加入日期、总浏览量、总下载量、备注等完整字段。',
      },
    ],
  },
  {
    id: 'materials',
    title: '资料管理',
    icon: 'ri-folder-upload-line',
    color: '#7c3aed',
    content: [
      {
        type: 'heading',
        title: '资料列表页',
      },
      {
        type: 'text',
        text: '展示所有已上传资料的完整列表，是后台最核心的操作页面之一。支持多级筛选、分类管理和快捷操作。',
      },
      {
        type: 'heading',
        title: '列表页结构',
      },
      {
        type: 'list',
        items: [
          '级别筛选按钮组：全部 / 普通 / 机密 — 带数量徽标，选中项有背景色区分（机密为红色系）',
          '工具栏：搜索框（按标题模糊匹配）+ 分类筛选下拉（二级分类）+ 管理分类按钮（打开分类管理器弹窗）+ 上传资料按钮',
          '表格列：资料（缩略图+标题）、一级/二级分类（显示两级分类路径）、三级分类（型号标签或「—」）、格式+级别标签、上传日期、浏览量、下载量、可下载开关、操作',
          '操作列：设为机密/普通（盾牌图标）、查看详情（眼睛图标）、编辑（铅笔图标）、删除（垃圾桶图标，二次确认弹窗）',
        ],
      },
      {
        type: 'heading',
        title: '上传资料',
      },
      {
        type: 'list',
        items: [
          '三栏布局：左侧预览卡片，右侧表单',
          '基本信息区块：标题（必填）、描述（选填）、标签（逗号分隔）',
          '分类设置区块：二级分类下拉选择（带一级分类分组）；选择后联动显示所属一级分类；若二级分类包含型号，显示三级分类（型号）下拉，选填',
          '文件类型与权限区块：文件类型下拉（PDF/视频/PPT/Excel/Word）；根据类型显示对应的水印和下载策略说明卡片；允许下载开关（E-learning 自动禁用）；PDF 额外显示「下载时叠加水印」开关；机密资料开关',
          '文件上传区块：E-learning 显示视频链接输入+视频文件拖拽上传+封面图上传+时长输入；其他类型显示文件拖拽上传区',
          '提交成功后显示成功状态页，1.8秒后自动跳转回列表',
        ],
      },
      {
        type: 'heading',
        title: '编辑资料',
      },
      {
        type: 'text',
        text: '页面采用三栏布局：左侧2/3为基本信息和文件来源编辑区，右侧1/3为设置面板。基本信息包括标题、描述；分类设置使用 CategoryTreeSelector 组件（一级/二级/三级联动下拉）；文件来源区支持替换文件（非视频）或修改视频链接/替换视频文件（视频）。右侧面板包含：级别设置（普通/机密单选）、下载权限开关（非视频）、水印设置区（预览水印始终开启不可编辑；PDF 支持下载水印开关；其他格式显示不支持说明）、当前文件信息只读展示。',
      },
      {
        type: 'heading',
        title: '资料详情页',
      },
      {
        type: 'list',
        items: [
          '三栏布局：左侧2/3为文件预览区，右侧1/3为信息和设置面板',
          '预览区：非视频显示缩略图+模拟文档骨架屏；视频显示视频播放器或封面图+播放按钮覆盖层',
          '右侧数据统计：浏览量+下载量双卡片；下载率进度条（下载量/浏览量）',
          '文件信息：格式标签、级别标签、文件大小、完整分类路径、上传者、上传日期',
          '权限设置：下载状态标签、机密授权人数（机密资料时显示）',
          '底部「编辑资源」按钮跳转编辑页',
        ],
      },
      {
        type: 'heading',
        title: '分类管理器',
      },
      {
        type: 'text',
        text: '弹窗组件，维护三级分类体系。一级分类固定（产品线、培训与学习、软件与系统），不可增删。可新增二级分类（选择所属一级分类，输入名称后创建）。每个二级分类支持展开/收起，展开后显示其下的产品型号列表，支持新增、编辑、删除型号。编辑和删除操作均为行内即时编辑模式（输入框+确认/取消按钮）。删除二级分类时会连带删除其下所有型号，有二次确认。',
      },
      {
        type: 'heading',
        title: '业务规则',
      },
      {
        type: 'rule',
        items: [
          '分类层级：一级分类（产品线大类）→ 二级分类（子分类，如 CT Systems）→ 三级分类（产品型号，如 InsitumCT 338）',
          '三级分类为选填项，适用于全系列的通用资料可不指定型号',
          'E-learning 分类的资料自动识别为视频类型，downloadAllowed 自动设为 false',
          '删除资料操作不可撤销，删除后立即从列表移除',
          '设为机密的资料会自动保留 confidentialAgents 数组；改为普通时清空该数组',
        ],
      },
    ],
  },
  {
    id: 'watermark',
    title: '水印与下载策略',
    icon: 'ri-shield-check-line',
    color: '#b45309',
    content: [
      {
        type: 'heading',
        title: '水印内容配置',
      },
      {
        type: 'text',
        text: '水印设置页面采用左右双栏布局。左侧为配置区，右侧为实时效果预览区。',
      },
      {
        type: 'heading',
        title: '配置区结构',
      },
      {
        type: 'list',
        items: [
          '顶部信息卡片：说明动态水印的溯源价值',
          '水印字段列表：三个字段项，每项包含图标、名称、描述、示例值、开关按钮',
          '字段1「用户账号」：动态字段，显示当前登录用户的邮箱；用于外传溯源',
          '字段2「公司名称」：动态字段，显示代理商所属公司英文名称',
          '字段3「访问日期」：动态字段，显示预览当天的实际日期',
          '每个字段独立开启/关闭，点击「保存设置」后显示成功状态',
        ],
      },
      {
        type: 'heading',
        title: '效果预览区',
      },
      {
        type: 'list',
        items: [
          '模拟 A4 比例文档预览，内部为灰色骨架屏模拟真实文档内容',
          '水印以倾斜文字形式叠加在文档上方，字体大小/透明度/旋转角度由系统固定（非用户可调）',
          '水印内容实时反映左侧开关状态，所有开启字段以「 · 」连接显示',
          '右侧底部显示水印内容拆解列表，每条显示字段图标、示例值、动态标记、开关状态',
        ],
      },
      {
        type: 'heading',
        title: '文件类型策略总览',
      },
      {
        type: 'text',
        text: '页面底部以 4 列卡片展示不同文件类型的差异化策略：',
      },
      {
        type: 'table',
        rows: [
          { label: 'PDF 文件', desc: '在线强制水印；下载水印可在资料编辑页单独开启/关闭；下载权限可配置' },
          { label: '视频资料', desc: '在线播放强制水印；默认禁止下载；无需配置下载水印' },
          { label: 'PPT 文件', desc: '在线水印；管理员自行决定是否允许下载；下载后为原始格式，无法嵌入水印' },
          { label: 'Word / Excel', desc: '在线水印；强制仅支持在线浏览，禁止下载' },
        ],
      },
      {
        type: 'heading',
        title: '业务规则',
      },
      {
        type: 'rule',
        items: [
          '在线预览水印对所有格式均强制叠加，不可关闭',
          '水印样式（字体大小15px、透明度12%、旋转-35度）由系统统一设定，用户不可调整',
          '下载水印仅 PDF 格式支持嵌入；其他格式下载后为原始文件',
          '水印内容中的动态字段（用户账号、公司名称、访问日期）随当前登录用户实时变化',
        ],
      },
    ],
  },
  {
    id: 'frontend',
    title: '代理商前端',
    icon: 'ri-global-line',
    color: '#0891b2',
    content: [
      {
        type: 'heading',
        title: '资料库首页',
      },
      {
        type: 'text',
        text: '代理商登录后进入的唯一页面。左侧为分类筛选和排序面板，右侧为资料卡片网格。顶部有全局搜索栏。',
      },
      {
        type: 'heading',
        title: '页面结构',
      },
      {
        type: 'list',
        items: [
          '顶部 PortalNav 导航栏：包含搜索输入框、语言切换、用户头像下拉（个人资料/退出）',
          '左侧 Sidebar：一级分类展开显示二级分类；二级分类展开显示三级型号；支持「最新/热门/字母」排序',
          '右侧主内容区：资料卡片网格（响应式：1/2/3/4列）；每张卡片显示缩略图、标题、分类标签、浏览量、下载按钮',
          '无结果状态：显示空状态插图+清除筛选按钮',
        ],
      },
      {
        type: 'heading',
        title: '文件预览',
      },
      {
        type: 'list',
        items: [
          '非视频：点击卡片打开 PreviewModal，显示带水印覆盖层的文档预览',
          '视频：点击卡片打开 VideoPlayerModal，内置视频播放器播放，视频画面上叠加动态水印',
          '下载按钮：仅 downloadAllowed=true 的非视频资料显示下载按钮；E-learning 隐藏下载按钮',
          '每次预览和下载都会通过 useActivityTracker 触发活动追踪，记录到 mockUserActivity',
        ],
      },
      {
        type: 'heading',
        title: '业务规则',
      },
      {
        type: 'rule',
        items: [
          '代理商仅能看到其 allowedModels 范围内且被授权访问的资料（普通资料自动可见，机密资料需单独授权）',
          '水印自动叠加在所有预览和播放场景中，代理商无法关闭或绕过',
          '下载行为会触发活动追踪，管理员可在后台查看每位代理商的文件访问明细',
        ],
      },
    ],
  },
  {
    id: 'data-model',
    title: '数据模型与接口',
    icon: 'ri-database-2-line',
    color: '#6366f1',
    content: [
      {
        type: 'heading',
        title: '核心数据实体',
      },
      {
        type: 'table',
        rows: [
          { label: 'Agent', desc: '代理商账号：id, name, email, company, region, status, expiryDate, memberSince, totalViews, totalDownloads, lastActive, notes, socialPlatform, socialAccount, permissions, allowedModels' },
          { label: 'AdminMaterial', desc: '后台资料：id, title, type, category, modelCategory, primaryCategory, fileSize, uploadDate, uploadedBy, views, downloads, previewAllowed, downloadAllowed, thumbnail, isVideo, videoDuration, videoUrl, description, tags, confidential, confidentialAgents' },
          { label: 'CategoryNode', desc: '分类树节点：id, label, icon, color, subCategories[]（含 models[]）' },
          { label: 'UserActivity', desc: '代理商活动：userId, totalPreviews, totalDownloads, files[]（fileId, fileTitle, fileCategory, fileType, previewCount, downloadCount, lastAccessed）' },
          { label: 'RecentActivity', desc: '后台操作日志：id, actor_name, actor_id, action_type, target_type, target_name, target_id, timestamp' },
        ],
      },
      {
        type: 'heading',
        title: '状态流转',
      },
      {
        type: 'table',
        rows: [
          { label: '代理商状态', desc: 'active → disabled（手动禁用，可恢复）；active → expired（有效期到期，续期后恢复）' },
          { label: '资料级别', desc: 'normal → confidential（设置后需手动添加授权代理商）；confidential → normal（清空授权列表）' },
          { label: '下载权限', desc: '仅对非视频资料可切换；E-learning 强制锁定为仅浏览' },
        ],
      },
      {
        type: 'heading',
        title: '权限计算逻辑',
      },
      {
        type: 'rule',
        items: [
          '代理商可见资料 = （普通资料 OR 机密资料且 confidentialAgents 包含该代理商ID）AND 分类在 allowedModels 范围内',
          'disabled / expired 状态的代理商无法登录，所有资料不可见',
          'pending 状态的代理商理论上待审核，当前实现中视为可登录（需后续完善审核流程）',
          '下载权限独立控制：即使资料可见，downloadAllowed=false 时也只能预览不能下载',
        ],
      },
    ],
  },
];